import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, MapPin, Calendar } from 'lucide-react';
import { getStats } from '../lib/api';

const Impact = () => {
  const [stats, setStats] = useState({
    athletes_helped: 200,
    provinces_reached: 15,
    success_rate: 85,
    years_active: 5,
  });

  useEffect(() => {
    getStats()
      .then((res) => setStats(res.data))
      .catch(() => {});
  }, []);

  const statCards = [
    {
      icon: <Users className="w-8 h-8" />,
      number: `${stats.athletes_helped}+`,
      label: 'Atletas Beneficiados',
      description: 'Jóvenes deportistas recibiendo apoyo integral',
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      number: `${stats.provinces_reached}`,
      label: 'Provincias',
      description: 'Presencia en todo el interior argentino',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      number: `${stats.success_rate}%`,
      label: 'Tasa de Éxito',
      description: 'Atletas que logran contratos profesionales',
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      number: `${stats.years_active}`,
      label: 'Años de Trayectoria',
      description: 'Transformando vidas',
    },
  ];

  return (
    <section id="impacto" className="py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sky-600 font-semibold text-sm uppercase tracking-wide mb-2 block">
            Nuestro Impacto
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Resultados que Transforman
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Cada número representa un sueño hecho realidad
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto mb-20">
          {statCards.map((stat, index) => (
            <div
              key={index}
              className="text-center p-8 rounded-2xl bg-gradient-to-b from-sky-50 to-white border-2 border-sky-100 hover:border-sky-400 hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-sky-400 to-sky-600 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300">
                {stat.icon}
              </div>
              <div className="text-5xl font-bold text-gray-900 mb-2">{stat.number}</div>
              <div className="text-lg font-semibold text-gray-700 mb-2">{stat.label}</div>
              <div className="text-sm text-gray-500">{stat.description}</div>
            </div>
          ))}
        </div>

        {/* Impact Image Grid */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 relative overflow-hidden rounded-2xl shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1600077063877-22118d6290eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHx5b3VuZyUyMHNvY2NlciUyMHBsYXllcnMlMjB0cmFpbmluZ3xlbnwwfHx8fDE3ODI2ODIzNjl8MA&ixlib=rb-4.1.0&q=85"
                alt="Equipo en acción"
                className="w-full h-[400px] object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sky-900/80 via-sky-900/40 to-transparent flex items-end">
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Trabajo en Equipo</h3>
                  <p className="text-sky-100">Desarrollando valores y habilidades colectivas</p>
                </div>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1763639700421-070686cd1b81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHx5b3V0aCUyMGF0aGxldGVzJTIwYWNoaWV2ZW1lbnR8ZW58MHx8fHwxNzgyNjgyNDA0fDA&ixlib=rb-4.1.0&q=85"
                alt="Celebración de logros"
                className="w-full h-[400px] object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sky-900/80 via-sky-900/40 to-transparent flex items-end">
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Logros</h3>
                  <p className="text-sky-100">Celebrando cada victoria</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial */}
        <div className="mt-20 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-sky-600 to-blue-600 rounded-2xl p-8 sm:p-12 text-white shadow-2xl">
            <div className="text-6xl font-bold text-white/20 mb-4">“</div>
            <p className="text-2xl sm:text-3xl font-semibold mb-6 leading-relaxed">
              Cada donación no solo ayuda a un atleta, construye el futuro del deporte argentino.
            </p>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                FD
              </div>
              <div>
                <div className="font-bold text-lg">Fundación Futuros del Deporte</div>
                <div className="text-sky-100">Equipo Directivo</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Impact;
